import { createContext, useCallback, useContext, useRef, useState } from 'react';

import { useEmailVerification } from '@/hooks/useEmailVerification';
import { VerifyEmailDialog } from './VerifyEmailDialog';

const VerifyEmailContext = createContext(null);

/**
 * Owns the deferred-verification UI (NEA-5773): the code-entry dialog and the
 * "go live" gate. Mounted once inside AppLayout so every dashboard page can:
 *
 *   - `openVerifyDialog()`  — open the dialog directly (the banner CTA).
 *   - `requireVerifiedEmail(action)` — run `action` immediately if the email is
 *     already verified, otherwise open the dialog and run `action` once
 *     verification succeeds. This is the gate the "launch storefront / go live"
 *     buttons funnel through.
 *
 * The banner itself lives in AppLayout's <main> (alongside the other banners)
 * and calls `openVerifyDialog()`.
 */
export function VerifyEmailProvider({ children }) {
	const { needsVerification } = useEmailVerification();
	const [open, setOpen] = useState(false);
	const pendingActionRef = useRef(null);

	const openVerifyDialog = useCallback(() => {
		pendingActionRef.current = null;
		setOpen(true);
	}, []);

	const requireVerifiedEmail = useCallback(
		(action) => {
			// Always return a promise so callers can `.then()`/`await` uniformly
			// (e.g. ChannelDetail does `enable(id).then(...)`, useChannelActions
			// awaits). Verified → run now and adopt the action's result. Unverified
			// → resolve only once the deferred action runs after a successful
			// verification (stays pending forever if the dialog is dismissed).
			if (!needsVerification) {
				return Promise.resolve(action?.());
			}
			return new Promise((resolve) => {
				pendingActionRef.current = () => resolve(action?.());
				setOpen(true);
			});
		},
		[needsVerification]
	);

	const handleVerified = useCallback(() => {
		const action = pendingActionRef.current;
		pendingActionRef.current = null;
		action?.();
	}, []);

	const handleOpenChange = useCallback((next) => {
		setOpen(next);
		if (!next) pendingActionRef.current = null;
	}, []);

	return (
		<VerifyEmailContext.Provider value={{ needsVerification, openVerifyDialog, requireVerifiedEmail }}>
			{children}
			<VerifyEmailDialog open={open} onOpenChange={handleOpenChange} onVerified={handleVerified} />
		</VerifyEmailContext.Provider>
	);
}

export function useVerifyEmail() {
	const ctx = useContext(VerifyEmailContext);
	// Pages outside the provider (e.g. onboarding) should never gate, so fall
	// back to a no-op gate that just runs the action.
	if (!ctx) {
		return {
			needsVerification: false,
			openVerifyDialog: () => {},
			requireVerifiedEmail: (action) => Promise.resolve(action?.())
		};
	}
	return ctx;
}
